// src/pages/FavoritesPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ---- slug helpers (must match Dashboard) ----
const slugify = (s = '') =>
  String(s).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const toSlug = (item) => {
  const name  = (item?.name  || '').trim();
  const brand = (item?.brand || '').trim();
  const image = (item?.image || '').trim();
  const key =
    (brand && name && `${brand}-${name}`) ||
    name ||
    brand ||
    image ||
    JSON.stringify(item || {});
  return slugify(key);
};

// read favorites passed via navigate state, else from localStorage
const readPassedFavorites = (state) => {
  if (state && Array.isArray(state.favorites)) return state.favorites;
  try {
    const v = JSON.parse(localStorage.getItem('wrap_favorites') || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [wraps, setWraps] = useState([]);
  const [loading, setLoading] = useState(true);

  // keep as Set for O(1) checks & easy removal
  const [favSet, setFavSet] = useState(() => new Set(readPassedFavorites(state)));

  // fetch wraps
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch((process.env.PUBLIC_URL || '') + '/wraps.json', { cache: 'no-store' });
        const json = await res.json();
        if (mounted) {
          const arr = Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []);
          setWraps(arr);
        }
      } catch {
        if (mounted) setWraps([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // persist favorites whenever favSet changes
  useEffect(() => {
    try { localStorage.setItem('wrap_favorites', JSON.stringify(Array.from(favSet))); } catch {}
  }, [favSet]);

  // only include wraps whose stable slug is in favorites
  const items = useMemo(() => {
    if (favSet.size === 0) return [];
    return wraps.filter((w) => favSet.has(toSlug(w)));
  }, [wraps, favSet]);

  // actions
  const removeFavorite = (w) => {
    const slug = toSlug(w);
    if (!favSet.has(slug)) return;
    const next = new Set(favSet);
    next.delete(slug);
    setFavSet(next);
  };

  const clearAll = () => setFavSet(new Set());

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
        >
          ← Back
        </button>
        <h2 style={{ margin: 0 }}>My Favorites</h2>
        {items.length > 0 && (
          <button
            onClick={clearAll}
            style={{ marginLeft: 'auto', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#dc2626' }}
            title="Remove all favorites"
          >
            Clear all
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ color: '#6b7280' }}>
          No favorites found.
          <div style={{ fontSize: 12, marginTop: 6 }}>
            Tip: Mark some wraps as favorites on the Dashboard.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {items.map((w, i) => (
            <div key={`${toSlug(w)}-${i}`} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ height: 140, background: '#f3f4f6', display: 'grid', placeItems: 'center' }}>
                {w.image ? (
                  <img src={w.image} alt={w.name || 'wrap'} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: '#9ca3af' }}>No image</span>
                )}
              </div>
              <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{w.name || 'Untitled wrap'}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{w.brand || ''}</div>
                </div>
                <button
                  onClick={() => removeFavorite(w)}
                  style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
                  title="Remove from favorites"
                >
                  ✖ Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
